import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useRuntimeConstants } from "../runtimeConstants/RuntimeConstantsContext";
import { useFeaturesReady } from "../featuresReady/FeaturesReadyContext";
import { getFeature } from "../../utils/features";
import { isProductUnavailable } from "../../../utils/productUtils";
import { Product } from "../../types/productTypes/product";
import {
  StylingLookDataFromServer,
  StylingLook,
  StylingLookWithStockPrice,
} from "../../types/productTypes/stylingLookProduct";
import setWaysToStyleTracking from "../../analytics/pageLoad/waysToStyle";

interface WaysToStyleContextType {
  shouldShowWaysToStyle: boolean;
  stylingLooks: StylingLook[];
  stylingLooksWithStockPrice: StylingLookWithStockPrice[];
  stockPriceByLookId: Record<number, StylingLookWithStockPrice>;
  setStockPriceForLook: (
    lookId: number,
    data: StylingLookWithStockPrice,
  ) => void;
  resetStockPriceData: () => void;
}

export const WaysToStyleContext = createContext<WaysToStyleContextType>({
  shouldShowWaysToStyle: false,
  stylingLooks: [],
  stylingLooksWithStockPrice: [],
  stockPriceByLookId: {},
  setStockPriceForLook: () => {},
  resetStockPriceData: () => {},
});

export const isWaysToStyleFeatureEnabled = () =>
  getFeature("pdp-ways-to-style")?.isEnabled();

export const WaysToStyleProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isMobile, product, stylingLooksData, isMixAndMatch } =
    useRuntimeConstants() as {
      isMobile: boolean;
      product: Product;
      stylingLooksData: StylingLookDataFromServer[];
      isMixAndMatch: boolean;
    };

  const [shouldShowWaysToStyle, setShouldShowWaysToStyle] = useState(false);
  const [stylingLooks, setStylingLooks] = useState<StylingLook[]>([]);
  const [stockPriceByLookId, setStockPriceByLookId] = useState<
    Record<number, StylingLookWithStockPrice>
  >({});
  const { areFeaturesReady } = useFeaturesReady() as {
    areFeaturesReady: boolean;
  };

  const [isWaysToStyleEnabled, setIsWaysToStyleEnabled] = useState(false);

  const setStockPriceForLook = useCallback(
    (lookId: number, data: StylingLookWithStockPrice) => {
      setStockPriceByLookId((prev) => ({ ...prev, [lookId]: data }));
    },
    [],
  );

  const resetStockPriceData = useCallback(() => {
    setStockPriceByLookId({});
  }, []);

  const stylingLooksWithStockPrice = useMemo(
    () =>
      stylingLooks
        .filter((look) => stockPriceByLookId[look.id])
        .map((look) => stockPriceByLookId[look.id]),
    [stylingLooks, stockPriceByLookId],
  );

  useEffect(() => {
    if (areFeaturesReady) {
      setIsWaysToStyleEnabled(isWaysToStyleFeatureEnabled());
    }
  }, [areFeaturesReady]);

  useEffect(() => {
    if (isMobile && areFeaturesReady && isWaysToStyleEnabled) {
      setShouldShowWaysToStyle(true);
    }
  }, [areFeaturesReady, isWaysToStyleEnabled, isMobile]);

  useEffect(() => {
    const updateStylingLooks = () => {
      const isUnavailable = isProductUnavailable(product);
      const shouldMapStylingLooks =
        stylingLooksData?.length && !isMixAndMatch && !isUnavailable;

      if (shouldMapStylingLooks) {
        const mappedLooks = mapStylingLooks(stylingLooksData);

        setStylingLooks(mappedLooks);
        if (isMobile && mappedLooks.length > 0) {
          setWaysToStyleTracking(mappedLooks.length);
        }
      }
    };

    if (areFeaturesReady && isWaysToStyleEnabled) {
      updateStylingLooks();
    }
  }, [
    areFeaturesReady,
    isWaysToStyleEnabled,
    stylingLooksData,
    product,
    isMixAndMatch,
    isMobile,
  ]);

  return (
    <WaysToStyleContext.Provider
      value={{
        shouldShowWaysToStyle,
        stylingLooks,
        stylingLooksWithStockPrice,
        stockPriceByLookId,
        setStockPriceForLook,
        resetStockPriceData,
      }}
    >
      {children}
    </WaysToStyleContext.Provider>
  );
};

const mapStylingLooks = (
  stylingLooksData: StylingLookDataFromServer[],
): StylingLook[] => {
  if (!stylingLooksData) {
    return [];
  }

  return stylingLooksData
    .map((look) => ({
      id: look.id,
      isHeroLook: look.isHeroLook,
      labelTranslationKey: look.labelTranslationKey,
      products:
        look.looksProductsFromServer ||
        look.stylingLookProductsFromServer ||
        [],
    }))
    .filter(
      (look) =>
        look.products.length && look.products.some((product) => product.isHero),
    )
    .sort((a, b) => Number(b.isHeroLook) - Number(a.isHeroLook));
};

export const useWaysToStyle = () => useContext(WaysToStyleContext);
